import React, { useState, useEffect, useRef } from 'react';
import { Box, TextField, Button, Alert, IconButton, InputAdornment, Typography, Container, Paper, FormControlLabel, Checkbox, Switch } from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
// Cookies are now set by the backend as HttpOnly cookies - no client-side cookie setting needed

const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [staySignedIn, setStaySignedIn] = useState(false);
  const [useRemoteApi, setUseRemoteApi] = useState(
    localStorage.getItem('useRemoteApi') === 'true'
  );
  const navigate = useNavigate();
  const emailInputRef = useRef<HTMLInputElement>(null);

  // Get API URL based on current selection
  const apiBaseUrl = useRemoteApi 
    ? process.env.REACT_APP_REMOTE_API_URL 
    : process.env.REACT_APP_LOCAL_API_URL;

  // Update localStorage when API selection changes
  useEffect(() => {
    localStorage.setItem('useRemoteApi', useRemoteApi.toString());
  }, [useRemoteApi]);

  // Focus email input only once on initial mount when window is visible
  useEffect(() => {
    // Only focus if window is visible and we haven't focused before
    if (emailInputRef.current && document.visibilityState === 'visible') {
      // Small delay to ensure the component is fully rendered
      const timeoutId = setTimeout(() => {
        if (emailInputRef.current && document.visibilityState === 'visible') {
          emailInputRef.current.focus();
        }
      }, 100);
      return () => clearTimeout(timeoutId);
    }
  }, []); // Empty dependency array - only run on mount

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${apiBaseUrl}/api/auth/login-admin`, {
        email,
        password,
        staySignedIn
      }, {
        withCredentials: true // Required to receive HttpOnly cookies
      });
      
      // Tokens are now set as HttpOnly cookies by the backend
      // No need to store in localStorage or set cookies client-side
      if (response.data.user) {
        navigate('/');
      } else {
        setError('Invalid response from server');
      }
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Login failed. Please check your credentials.';
      setError(errorMessage);
    }
  };

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  return (
    <Container component="main" maxWidth="xs">
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Paper elevation={3} sx={{ p: 4, width: '100%' }}>
          <Typography component="h1" variant="h5" sx={{ mb: 3, textAlign: 'center' }}>
            Admin Login
          </Typography>
          
          <Box component="form" onSubmit={handleLogin} noValidate>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            
            <TextField
              margin="normal"
              required
              fullWidth
              id="email"
              label="Email Address"
              name="email"
              autoComplete="email"
              inputRef={emailInputRef}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            
            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label="Password"
              type={showPassword ? 'text' : 'password'}
              id="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={handleClickShowPassword}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            
            <Box sx={{ display: 'flex', flexDirection: 'column', mt: 1 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    value="staySignedIn"
                    color="primary"
                    checked={staySignedIn}
                    onChange={(e) => setStaySignedIn(e.target.checked)}
                  />
                }
                label="Stay signed in"
              />
              
              <Box sx={{ display: 'flex', flexDirection: 'column', mt: 1, p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                <Typography variant="body2" fontWeight="medium" sx={{ mb: 1 }}>
                  API Server
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      color: !useRemoteApi ? 'primary.main' : 'text.secondary',
                      fontWeight: !useRemoteApi ? 'medium' : 'normal'
                    }}
                  >
                    💻 Local
                  </Typography>
                  <Switch
                    checked={useRemoteApi}
                    onChange={(e) => setUseRemoteApi(e.target.checked)}
                    color="primary"
                  />
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      color: useRemoteApi ? 'primary.main' : 'text.secondary',
                      fontWeight: useRemoteApi ? 'medium' : 'normal'
                    }}
                  >
                    🌎 Remote
                  </Typography>
                </Box>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1, textAlign: 'center' }}>
                  {apiBaseUrl || 'Not set'}
                </Typography>
              </Box>
            </Box>
            
            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 2 }}
            >
              Sign In
            </Button>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default Login; 